import React from 'react'
import { Grid, Typography, MenuItem } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { makeStyles, Theme } from '@material-ui/core/styles'
import LiquidSelect from '@src/components/LiquidSelect'
import HoverToolTip from '@src/components/HoverToolTip'
import useTubeValidation from '../useTubeValidation'
import useFormikHelper from '../useFormikHelper'
import { splitMessages } from '../utils'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import {
  isBiTubeLightCyclerProduct,
  useProductFromQueryParam,
  numberOfTubesPerOligoText,
  shouldRenderSingleNumberOfTubes,
  checkForRedesignedBiTubeProduct,
} from '../ProductsConfiguration'
import { TubeProperties } from '../../../utils/StandardMTOEnum'
import { BiTubeProduct } from '../../BiTube/enum'

interface NumberOfTubesProps {}
const useStyle = makeStyles((theme: Theme) => {
  return {
    label: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 700,
    },
    infoIcon: {
      fontSize: theme.typography.pxToRem(19.25),
      color: theme.palette.primary.main,
      cursor: 'pointer',
      marginLeft: theme.spacing(1.25),
      marginBottom: theme.spacing(-0.75),
    },
    minimumYield: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 400,
      color: theme.palette.grey[500],
    },
    disabledLabel: {
      color: theme.palette.grey[400],
    },
    singleTubeStyle: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
  }
})

const NumberOfTubes: React.FC<NumberOfTubesProps> = () => {
  const formik = useFormikHelper()
  const classes = useStyle()
  const { validationAPI } = useTubeValidation()
  const product = useProductFromQueryParam()
  const biTubeLightCyclerProduct = isBiTubeLightCyclerProduct(product)
  const renderSingleNumberOfTubes = shouldRenderSingleNumberOfTubes(product)
  const isMirna = product === BiTubeProduct.mirna
  const sequenceType =
    biTubeLightCyclerProduct || isMirna
      ? TubeProperties.AntiSenseSequence
      : TubeProperties.Sequence

  const disabled =
    !formik.selectedOligoValues?.[sequenceType] ||
    formik.selectedOligoValues?.[sequenceType] === '' ||
    (formik.selectedOligoValues?.[sequenceType] !== '' &&
      !!formik.errors?.[sequenceType])
  const senseOrAntiSenseInvalid =
    product !== BiTubeProduct.mirna &&
    checkForRedesignedBiTubeProduct(product) &&
    (!formik.isValidSense() || !formik.isValidAntiSense())

  const onChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const tubes: number = e.target.value !== '' ? (e.target.value as number) : 1
    const tubeValue = tubes.toString()
    formik.setOligoFieldValue('tubes', tubeValue, false)
    //apply specification to other oligos
    if (formik.allValues.applyToNextOligos && formik.activeOligoIndex === 0) {
      const updatedValues = formik.allValues.oligoList.map((oligo) => ({
        ...oligo,
        tubes: tubeValue,
      }))
      formik.setFieldValue('oligoList', updatedValues, false)
    } else if (
      formik.allValues.applyToNextOligos &&
      formik.activeOligoIndex !== 0
    ) {
      formik.setFieldValue('applyToNextOligos', false, false)
    }

    validationAPI({
      ...formik.validationFields,
      tubes: tubeValue,
    })
  }
  const minimumYield =
    formik.selectedOligoValues?.listMinimumYield &&
    formik.selectedOligoValues?.tubes &&
    formik.selectedOligoValues?.listMinimumYield !== 0
      ? (
          formik.selectedOligoValues.listMinimumYield /
          parseInt(formik.selectedOligoValues.tubes)
        ).toFixed(2)
      : 0

  const errorList =
    ((formik.touched?.tubes ||
      (formik.selectedOligoValues?.tubes &&
        formik.selectedOligoValues?.tubes !== '')) &&
      formik.errors?.tubes &&
      splitMessages(formik.errors.tubes)) ||
    []
  const title = numberOfTubesPerOligoText(product)

  const numberOfTubesDropDown = (): React.ReactElement => {
    return (
      <>
        <Grid item xs={3}>
          <LiquidSelect
            filled
            onChange={onChange}
            value={formik.selectedOligoValues.tubes || 1}
            displayEmpty
            disabled={disabled || senseOrAntiSenseInvalid}
            // renderValue is a function used to juggle default and updated values on some components.
            // Natively it is intended to be typed as an 'any' for the underlying component or children to sort out.
            // https://mui.com/material-ui/api/select/?srsltid=AfmBOoqUtFRlYq_gcVdHPmqFooQ26YLTcNkmjshocvsYZ0HPm04LYjK0#select-prop-renderValue
            renderValue={(value: any) => value}
          >
            {formik.selectedOligoValues.listNumberOfTubes?.map((value) => (
              <MenuItem key={value} value={value}>
                <Typography className={disabled ? classes.disabledLabel : ''}>
                  {value}
                </Typography>
              </MenuItem>
            ))}
          </LiquidSelect>
        </Grid>
        <Grid item xs={12} container>
          <Grid item>
            {errorList.length > 0 && (
              <ValidationErrors errors={errorList} configuratorsRedesign />
            )}
          </Grid>
        </Grid>
        <Grid item xs={3}>
          {minimumYield !== 0 && (
            <Typography className={classes.minimumYield}>
              <FormattedMessage
                id="CONF_MIN_YIELD_PER_TUBE_UPDATED"
                defaultMessage="Minimum yield per tube = {minimumYield} OD"
                values={{ minimumYield: minimumYield }}
              />
            </Typography>
          )}
        </Grid>
      </>
    )
  }

  return (
    <Grid container direction="column">
      <Grid item xs={4}>
        <Grid container alignItems="center">
          <Grid item>
            <Typography
              className={`${classes.label} ${
                disabled ? classes.disabledLabel : ''
              }`}
            >
              {title}
            </Typography>
          </Grid>
          {!renderSingleNumberOfTubes && (
            <Grid item>
              <HoverToolTip
                fontSize="19.25"
                toolTipText={
                  <FormattedMessage
                    id="CONF_NUMBER_OF_TUBES_INFO_STANDARD"
                    defaultMessage="Amount will be split equally between tubes. Additional fees may apply."
                  />
                }
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      {renderSingleNumberOfTubes ? (
        <Grid item xs={3}>
          <Typography
            className={`${classes.singleTubeStyle} ${
              disabled ? classes.disabledLabel : ''
            }`}
          >
            {1}
          </Typography>
        </Grid>
      ) : (
        numberOfTubesDropDown()
      )}
    </Grid>
  )
}

export default NumberOfTubes
